import React from "react";
import { Helmet } from 'react-helmet-async';
import { FaFacebookF, FaLinkedinIn, FaMediumM, FaWhatsapp, FaSearch, FaMapMarkerAlt, FaBriefcase, FaArrowRight } from "react-icons/fa";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RotatingText from '../components/RotatingText';
import bgImage from "../assets/images/asdflobg.jpg";
import PlatformFeatures from "../components/PlatformFeatures";
import SignupModal from "../components/SignupModal";
import useAuthUser from "../hooks/useAuthUser";
import { auth } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReferralModal from "../components/ReferralModal";
import FindProfessionalsTab from "../components/FindProfessionalsTab";
import FindOrganizationsTab from "../components/FindOrganizationsTab";
import JobSearchWidget from "../components/jobs/JobSearchWidget";

// SEO Meta tags for the landing page
const seoKeywords = [
  'autism jobs India', 'hiring ABA therapists', 'special educator directory',
  'autism assessment services', 'autism therapy jobs', 'special education jobs',
  'autism support professionals', 'RCI registered therapists', 'speech therapy jobs',
  'occupational therapist jobs', 'behavior therapist jobs', 'autism center jobs',
  'find autism specialist', 'autism interventionist', 'child development jobs',
  'special needs educator jobs', 'autism support India', 'BCBA jobs India',
  'autism center directory', 'early intervention specialist'
].join(', ');

// Toast configuration is handled by the ToastContainer component


function Landing() {
  const user = useAuthUser();
  const [referralModal, setReferralModal] = React.useState({ open: false, role: null });
  const navigate = useNavigate();
  
  const [showTerms, setShowTerms] = React.useState(false);
  const [showSignupModal, setShowSignupModal] = React.useState(false);
  const [signupRole, setSignupRole] = React.useState(null);


  const handleOpenSignup = (role) => {
    if (user) {
      navigate('/app/profile', { replace: true });
      return;
    }
    
    setSignupRole(role);
    setShowSignupModal(true);
  };

  const handleCloseSignup = () => {
    setShowSignupModal(false);
    setSignupRole(null);
  };
  

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Autism Jobs & Therapy Services in India | ASDFlo</title>
        <meta name="description" content="Find verified autism therapists, special educators, and ABA jobs across India. Post jobs or connect with professionals – free on ASDFlo." />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content="Autism Jobs & Therapy Services in India | ASDFlo" />
        <meta property="og:description" content="Find verified autism therapists, special educators, and ABA jobs across India. Post jobs or connect with professionals – free on ASDFlo." />
        {bgImage && <meta property="og:image" content={`${window.location.origin}${bgImage}`} />}
        <meta property="og:site_name" content="ASDFlo" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Autism Jobs & Therapy Services in India | ASDFlo" />
        <meta name="twitter:description" content="Find verified autism therapists, special educators, and ABA jobs across India. Post jobs or connect with professionals – free on ASDFlo." />
        {bgImage && <meta name="twitter:image" content={`${window.location.origin}${bgImage}`} />}
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "ASDFlo",
            "url": window.location.origin,
            "description": "India's leading platform connecting parents, autism professionals, and therapy centers",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${window.location.origin}/search?q={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ASDFlo",
            "url": window.location.origin,
            "logo": `${window.location.origin}/logo.png`,
            "sameAs": [
              "https://www.facebook.com/asdflo",
              "https://www.linkedin.com/company/asdflo",
              "https://twitter.com/asdflo"
            ],
            "contactPoint": [{
              "@type": "ContactPoint",
              "telephone": "+91-8978733122",
              "contactType": "customer service",
              "availableLanguage": ["English", "Hindi"]
            }]
          })}
        </script>
      </Helmet>
      
      {/* Toast Notifications */}
      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Use the shared Header component */}
      <Header onLoginButtonClick={() => handleOpenSignup(null)} />

      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen w-full text-center overflow-hidden bg-gray-900">
        {/* Background Video with Enhanced Overlay */}
        <div className="absolute inset-0 w-full h-full z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: 'brightness(0.7)',
              transform: 'scale(1.05)'
            }}
            aria-label="Background video showing happy children with therapists"
          >
            <source src={`${process.env.PUBLIC_URL}/assets/videos/asdflovideo.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div 
            className="absolute inset-0 bg-gradient-to-br from-indigo-900/60 via-purple-900/50 to-blue-900/70"
            style={{
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)'
            }}
          />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl w-full px-6 py-24 sm:py-32">
          <div className="flex flex-col items-center justify-center">
            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 text-white tracking-tight">
              Empowering Neurodiverse Growth through Inclusive Tech
            </h1>
            
            {/* Rotating Mission Statements - Replaces subheading */}
            <div className="w-full">
              <div className="text-center">
                <RotatingText 
                  texts={[
                    "Schools: Hire special educators easily & efficiently",
                    "Hospitals: Find qualified autism specialists",
                    "Parents: Get assessments & connect with therapists",
                    "Professionals: Grow your practice & find opportunities",
                    "CDC: Build your specialized support team"
                  ]} 
                  interval={3500}
                  initialDelay={800}
                />
                <p className="text-blue-100 text-sm sm:text-base mt-2 animate-pulse">
                  Free for early adopters - First 3 months on us!
                </p>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center w-full max-w-2xl mb-16">
              <button
                onClick={() => navigate('/free-assessment')}
                className="group relative overflow-hidden px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium text-lg shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300"
                aria-label="Take the free M-CHAT autism screening test"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span className="text-xl">🩺</span>
                  Take the Free M-CHAT Autism Screening
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-700 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </button>
              
              <button
                onClick={() => handleOpenSignup('professional')}
                className="group relative px-8 py-4 rounded-full border-2 border-indigo-300/50 text-white font-medium text-lg hover:bg-white/5 hover:border-indigo-400/70 transition-all duration-300"
                aria-label="Join as a professional"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span className="opacity-80">👩‍⚕️</span>
                  Join as a Professional
                </span>
              </button>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-4 flex flex-col items-center">
              <div className="flex items-center space-x-1.5 text-gray-200">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
                <span className="ml-2 text-gray-200 text-sm font-medium tracking-wide">4.9/5 from 200+ reviews</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10 group">
          <button
            onClick={() => {
              const el = document.getElementById('features');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex flex-col items-center justify-center text-white/70 hover:text-white transition-colors duration-300"
            aria-label="Scroll to explore"
          >
            <span className="text-sm font-medium mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              Explore platform
            </span>
            <svg 
              className="w-8 h-8 animate-bounce-slow" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
          </button>
        </div>
        
        {/* Custom animation for scroll indicator */}
        <style jsx="true" global="true">{`
          @keyframes bounce-slow {
            0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
            40% {transform: translateY(-10px);}
            60% {transform: translateY(-5px);}
          }
          .animate-bounce-slow {
            animation: bounce-slow 2s infinite;
          }
        `}</style>
      </section>
      
      {/* Professionals Section */}
      <FindProfessionalsTab 
        showHeader={true}
        headerText="Meet Our Top Professionals"
        subheaderText="Connecting you with the best special needs experts across India"
        limitResults={6}
        onLoadMoreClick={!user ? (e) => {
          e.preventDefault();
          toast.info('Please sign up to see more professionals', {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setShowSignupModal(true);
        } : undefined}
      />
      
      {/* Organizations Section */}
      <FindOrganizationsTab 
        showHeader={true}
        headerText="Explore Partner Organizations"
        subheaderText="Discover child development centers and organizations making a difference"
        limitResults={6}
        onLoadMoreClick={!user ? (e) => {
          e.preventDefault();
          toast.info('Please sign up to see more organizations', {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setShowSignupModal(true);
        } : undefined}
      />
      
      {/* Jobs Section */}
      <section id="jobs" className="py-16 bg-gray-50">
        <JobSearchWidget 
          limitResults={3}
          onViewAllClick={!user ? (e) => {
            e.preventDefault();
            toast.info('Please sign up to see more job opportunities', {
              position: 'top-center',
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            setShowSignupModal(true);
          } : undefined}
        />
      </section>

      <ReferralModal open={referralModal.open} onClose={() => setReferralModal({ open: false, role: null })} defaultRole={referralModal.role} />

      {/* Features Section */}
      <PlatformFeatures onOpenSignup={handleOpenSignup} />

      {/* Video Section */}
      <section className="w-full py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Experience the asdflo Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how our platform transforms lives through personalized care and support
            </p>
          </div>
          <div className="max-w-4xl mx-auto rounded-xl overflow-hidden shadow-2xl bg-black">
            <div className="relative group cursor-pointer" onClick={(e) => {
              const video = e.currentTarget.querySelector('video');
              video.controls = true;
              video.play();
              e.currentTarget.querySelector('.play-overlay').classList.add('hidden');
            }}>
              <video
                id="asdflo-video"
                playsInline
                preload="metadata"
                className="w-full h-auto max-h-[80vh]"
                aria-label="asdflo platform demonstration video"
              >
                <source src={`${process.env.PUBLIC_URL}/assets/videos/asdflovideo.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="play-overlay absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 group-hover:bg-black/40">
                <div className="bg-white/90 hover:bg-white transition-all duration-300 rounded-full p-4 group-hover:scale-110">
                  <svg className="w-12 h-12 md:w-16 md:h-16 text-indigo-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signup Modal */}
      <SignupModal show={showSignupModal} onClose={handleCloseSignup} initialRole={signupRole} />

      {/* Footer */}
      <Footer />

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/918978733122" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-14 h-14 sm:w-16 sm:h-16 bg-green-500 text-white rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl hover:scale-105 transition-all z-30" 
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp className="w-8 h-8" />
      </a>
    </div>
  );
}

export default Landing;